/**
 * ChatBalanceNotice — the thin notice directly above the composer when the
 * wallet is low or empty. Works in BOTH test and production modes.
 *
 * RENAMED FROM TestModeBanner (2026-09-13). The old name said the opposite of
 * when it fires: it is a PRODUCTION low-balance notice, and its own header has
 * said so since it was written. A component whose name misdescribes it is how
 * this went unexamined long enough to accumulate the ask-owner defect below —
 * "the test-mode banner" is not something anyone re-reads.
 *
 * 2026-08-14 owner directive: chat never mentions the balance unless it is
 * low or out.
 * - LOW (0 < balance <= the user's auto top-up threshold; the caller decides):
 *   one light pr0-denominated nudge, dismissible (reappears on interaction).
 * - OUT (balance <= $0, `isCritical`): explains WHY sending is unavailable;
 *   NOT dismissible — the composer is blocked until a top-up/redeem.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { usdToWu, formatWu, WU_LABEL, WU_FULL_NAME } from '../../config/workUnits';

// ============================================================================
// Styled Components
// ============================================================================

const Banner = styled.div<{ $isCritical: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  margin-bottom: 8px;
  background: ${p => p.$isCritical ? '#FEF2F2' : '#FFFBEB'};
  border: 1px solid ${p => p.$isCritical ? '#FECACA' : '#FDE68A'};
  border-radius: 8px;
  font-size: 0.8125rem;
  color: ${p => p.$isCritical ? '#991B1B' : '#92400E'};
`;

const Badge = styled.span<{ $isCritical: boolean }>`
  background: ${p => p.$isCritical ? '#FEE2E2' : '#FEF3C7'};
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: lowercase;
  letter-spacing: 0.5px;
`;

const Message = styled.span`
  flex: 1;
`;

const ActionButton = styled.button<{ $isCritical: boolean }>`
  background: ${p => p.$isCritical ? '#DC2626' : '#D97706'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: inherit;
  opacity: 0.4;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============================================================================
// Component Props
// ============================================================================

interface ChatBalanceNoticeProps {
  /** Current balance in USD */
  balanceUsd: number;
  /** Whether the wallet is OUT (<= $0) — changes styling/messaging and disables dismissal */
  isCritical: boolean;
  /** Whether system is in testing mode (optional, for backward compatibility) */
  isTestingMode?: boolean;
  /** Whether banner should be shown (balance is below warning threshold) */
  shouldShow: boolean;
  /** Whether user has a payment method on file */
  hasPaymentMethod: boolean;
  /**
   * This reader cannot top up — the wallet is the organization's and they do
   * not hold billing.manage. Same answer the band at the top of the page uses
   * (lowBalanceAudience.isAskOwnerAudience), because until 2026-09-13 the two
   * disagreed: the band gave this reader honest ask-an-owner copy and no
   * button, while THIS notice — the one attached to the composer they were
   * trying to use — offered them a top-up button that dead-ends.
   */
  askOwner?: boolean;
  /** Callback when user clicks the action button */
  onNavigate: () => void;
  /** Key that changes on every interaction - resets dismissed state */
  interactionKey: string | number;
}

// ============================================================================
// Component
// ============================================================================

export const ChatBalanceNotice: React.FC<ChatBalanceNoticeProps> = ({
  balanceUsd,
  isCritical,
  isTestingMode,
  shouldShow,
  hasPaymentMethod,
  onNavigate,
  askOwner = false,
  interactionKey,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed state on EVERY interaction (new message, chat open/close, etc.)
  useEffect(() => {
    setDismissed(false);
  }, [interactionKey]);

  // Don't render if shouldn't show, or dismissed (dismissal only ever applies
  // to the LOW nudge — the OUT state is not dismissible).
  // Shows in BOTH test and production modes.
  // The ask-owner reader may dismiss even when critical: there is nothing for
  // them to DO about it, so an undismissable strip is just a permanent tax on
  // their screen. The band made the same call for the same reason.
  if (!shouldShow || (dismissed && (!isCritical || askOwner))) {
    return null;
  }

  const message = askOwner
    ? (isCritical
      ? `you're out of ${WU_FULL_NAME} — that's why sending is unavailable. an owner or admin can top up this workspace.`
      : `balance running low — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left. an owner or admin can top up.`)
    : (isCritical
      ? `you're out of ${WU_FULL_NAME} — that's why sending is unavailable. top up or redeem a code to resume instantly.`
      : `balance running low — ${formatWu(usdToWu(balanceUsd))} ${WU_LABEL} left`);
  const buttonText = hasPaymentMethod ? 'top up' : 'add payment method';

  return (
    <Banner $isCritical={isCritical}>
      <Badge $isCritical={isCritical}>{isCritical ? 'OUT' : 'LOW'}</Badge>
      <Message>{message}</Message>
      {/* No control for a reader who cannot use it — the repo's standing rule
          is never a control that fails after the click. */}
      {!askOwner && (
        <ActionButton $isCritical={isCritical} onClick={onNavigate}>
          {buttonText}
        </ActionButton>
      )}
      {(!isCritical || askOwner) && (
        <DismissButton onClick={() => setDismissed(true)} title="Dismiss">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </DismissButton>
      )}
    </Banner>
  );
};

export default ChatBalanceNotice;
